import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Workflow, WorkflowStage } from '@/hooks/useWorkflows';
import { Plus, Trash2, GripVertical, Undo, Workflow as WorkflowIcon, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { AutomationRulesEditor, WorkflowAutomation } from './AutomationRulesEditor';
import { useWorkflowAutomations } from '@/hooks/useWorkflowAutomations';

interface WorkflowEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflow: Workflow | null;
  onSave: (workflow: Workflow) => Promise<boolean>;
  onReset: () => Promise<boolean>;
}

const PRESET_COLORS = [
  '#3b82f6', // blue
  '#a855f7', // purple
  '#f97316', // orange
  '#22c55e', // green
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#ef4444', // red
  '#f59e0b', // amber
  '#ec4899', // pink
  '#8b5cf6', // violet
];

export const WorkflowEditorDialog = ({
  open,
  onOpenChange,
  workflow,
  onSave,
  onReset,
}: WorkflowEditorDialogProps) => {
  const [editedWorkflow, setEditedWorkflow] = useState<Workflow | null>(workflow);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [localAutomations, setLocalAutomations] = useState<WorkflowAutomation[]>([]);
  
  const { automations, saveAutomations } = useWorkflowAutomations(workflow?.id);

  useEffect(() => {
    setEditedWorkflow(workflow);
  }, [workflow]);

  useEffect(() => {
    setLocalAutomations(automations);
  }, [automations]);

  const handleSave = async () => {
    if (!editedWorkflow) return;
    
    if (editedWorkflow.stages.length === 0) {
      toast.error('Workflow must have at least one stage');
      return;
    }

    setSaving(true);
    const success = await onSave(editedWorkflow);
    
    if (success && editedWorkflow.id) {
      // Save automations after workflow is saved
      const stageIds = editedWorkflow.stages.map(s => s.id || s.stage_key);
      await saveAutomations(editedWorkflow.id, stageIds, localAutomations);
    }
    
    setSaving(false);
    
    if (success) {
      onOpenChange(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    const success = await onReset();
    setResetting(false);
    
    if (success) {
      onOpenChange(false);
    }
  };

  const updateStage = (index: number, field: keyof WorkflowStage, value: string) => {
    if (!editedWorkflow) return;
    
    const newStages = [...editedWorkflow.stages];
    newStages[index] = {
      ...newStages[index],
      [field]: value,
    };
    
    setEditedWorkflow({
      ...editedWorkflow,
      stages: newStages,
    });
  };

  const addStage = () => {
    if (!editedWorkflow) return;
    
    const newStage: WorkflowStage = {
      stage_name: 'New Stage',
      stage_key: `stage_${editedWorkflow.stages.length + 1}`,
      stage_order: editedWorkflow.stages.length + 1,
      color: PRESET_COLORS[editedWorkflow.stages.length % PRESET_COLORS.length],
      job_status: 'new',
    };
    
    setEditedWorkflow({
      ...editedWorkflow,
      stages: [...editedWorkflow.stages, newStage],
    });
  };

  const removeStage = (index: number) => {
    if (!editedWorkflow) return;
    
    const newStages = editedWorkflow.stages.filter((_, i) => i !== index);
    setEditedWorkflow({
      ...editedWorkflow,
      stages: newStages,
    });
  };

  const moveStage = (index: number, direction: 'up' | 'down') => {
    if (!editedWorkflow) return;
    
    const newStages = [...editedWorkflow.stages];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newStages.length) return;
    
    [newStages[index], newStages[targetIndex]] = [newStages[targetIndex], newStages[index]];
    
    setEditedWorkflow({
      ...editedWorkflow,
      stages: newStages,
    });
  };

  if (!editedWorkflow) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto glass-card">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle className="text-2xl">
              Edit Workflow
            </DialogTitle>
          </div>
          <DialogDescription>
            Customize your workflow stages, colors, order, and automation rules
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="stages" className="py-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="stages" className="flex items-center gap-2">
              <WorkflowIcon className="w-4 h-4" />
              Stages
            </TabsTrigger>
            <TabsTrigger value="automations" className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Automations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stages" className="space-y-6 mt-4">
          {/* Workflow Name */}
          <div className="space-y-2">
            <Label htmlFor="workflow-name">Workflow Name</Label>
            <Input
              id="workflow-name"
              value={editedWorkflow.name}
              onChange={(e) => setEditedWorkflow({ ...editedWorkflow, name: e.target.value })}
              placeholder="Enter workflow name"
            />
          </div>

          {/* Stages */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Stages</Label>
              <Button onClick={addStage} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Add Stage
              </Button>
            </div>

            <div className="space-y-3">
              {editedWorkflow.stages.map((stage, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card/50"
                >
                  {/* Drag Handle */}
                  <div className="flex flex-col gap-1 pt-2">
                    <button
                      onClick={() => moveStage(index, 'up')}
                      disabled={index === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <GripVertical className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveStage(index, 'down')}
                      disabled={index === editedWorkflow.stages.length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <GripVertical className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Stage Fields */}
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Stage Name</Label>
                      <Input
                        value={stage.stage_name}
                        onChange={(e) => updateStage(index, 'stage_name', e.target.value)}
                        placeholder="Stage name"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label className="text-xs">Key</Label>
                      <Input
                        value={stage.stage_key}
                        onChange={(e) => updateStage(index, 'stage_key', e.target.value)}
                        placeholder="stage_key"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Color</Label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={stage.color}
                          onChange={(e) => updateStage(index, 'color', e.target.value)}
                          className="w-12 h-10 rounded border border-border cursor-pointer"
                        />
                        <Input
                          value={stage.color}
                          onChange={(e) => updateStage(index, 'color', e.target.value)}
                          placeholder="#3b82f6"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeStage(index)}
                    className="text-destructive hover:text-destructive mt-6"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          </TabsContent>

          <TabsContent value="automations" className="space-y-6 mt-4">
            <AutomationRulesEditor
              stages={editedWorkflow?.stages || []}
              automations={localAutomations}
              onAutomationsChange={setLocalAutomations}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={resetting || saving}
            className="mr-auto"
          >
            <Undo className="w-4 h-4 mr-2" />
            {resetting ? 'Resetting...' : 'Reset to Default'}
          </Button>
          
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
